import { ENV_KEY } from './config';
import type { Plugins } from './plugins';
import type { PluginFunction } from '@kastjs/runtime-core';

export interface UserDataInfo {
  /**
   * @description 用户头像
   */
  avatarUrl: string;
  /**
   * @description 用户邮箱
   */
  email: string;
  /**
   * @description 快手用户ID
   */
  ksUserId: number;
  /**
   * @description 姓名
   */
  name: string;
}

export type BaseOptions = {
  /**
   * @description 当前运行环境，如果没有传入
   *              如果是主应用，则会根据当前域名判断
   *                       prt: ad-crm.prt.kuaishou.com,crm-prt.prt.kuaishou.com
   *                   staging: ad-crm.staging.kuaishou.com,ad-crm-staging.staging.kuaishou.com
   *                      beta: crm-beta.corp.kuaishou.com, beta-operation-e.corp.kuaishou.com
   *                production: crm.e.kuaishou.com, ad-crm.corp.kuaishou.com
   *                     local: 以上情况都不是
   *              如果是子应用，则继承主应用
   * @default local
   */
  env?: 'prt' | 'staging' | 'beta' | 'production' | 'local';
  /**
   * @description 当前用户信息
   *              如果不传：
   *              主应用自动获取
   *              子应用从主应用上继承
   */
  userData?: UserDataInfo;
  /**
   * @description 当前请求的地址前缀
   *              如果是子应用，并且没有制定，则继承主应用
   * @default 当前env对应的后端网关地址
   *                 prt  'https://capi.prt.kuaishou.com',
   *             staging  'https://capi.staging.kuaishou.com'
   *                beta  'https://capi-beta.corp.kuaishou.com'
   *          production  'https://capi.e.kuaishou.com'
   *               local  ''
   */
  gatewayUrl?: string;
  /**
   *  @description 当前系统在UPM系统上面的key值
   *  @default 5
   */
  systemId?: number;
  /**
   * @description 当前系统在UPM上面的子系统key值
   *              如果是子应用，则继承主应用
   */
  subSystemName?: string;
  /**
   * @description 是否开启参数logger和初始化logger
   *              默认：production 为 false，其他环境为 true
   */
  logger?: boolean;
  /**
   * @description 更新内部状态，一般用于切换业务线的时候，刷新内部状态使用
   */
  reflush?: boolean;
};

export type KContext = Parameters<PluginFunction<any>>[0];

export type CRMOptions = BaseOptions & Omit<Plugins, 'micro'>;

export type InnerOptions = BaseOptions & {
  /**
   * @description 是否是主应用,如财神V2，门户Portal
   */
  isMain: boolean;
  /**
   * @description 是否是门户业务线
   *              需要在门户的 appList 里面指定 enabledMenu = 1
   * @default false
   */
  isPortalBiz: boolean;
  /**
   * @description 当前的UPM系统值
   */
  currentSubSystemName: string;
  /**
   * 当前所在环境
   * crm     财神
   * portal  门户
   * unknown 未知
   */
  source?: 'crm' | 'portal' | 'unknown';
  /**
   * 是否Kim左侧侧边栏
   */
  isKimLeftSideBar?: boolean;
};

enum UpmBiz {
  '财神CRM' = '财神CRM',
  // 品运工作台
  CRM_BRAND = 'CRM_BRAND',
  // 招商工作台
  CRM_MERCHANTS = 'CRM_MERCHANTS',
  // 渠道crm
  AGENT_CHANNEL = 'AGENT_CHANNEL',
}

// 业务baseNameMap
export const bizBaseNameMap: Record<UpmBiz, string> = {
  [UpmBiz.财神CRM]: '/crm-v2',
  [UpmBiz.AGENT_CHANNEL]: '/agent',
  [UpmBiz.CRM_MERCHANTS]: '/merchants',
  [UpmBiz.CRM_BRAND]: '/brand',
};

const commonHosts = {
  staging: 'https://ad-crm.staging.kuaishou.com',
  prt: 'https://ad-crm.prt.kuaishou.com',
  beta: 'https://crm-beta.corp.kuaishou.com',
  production: 'https://crm.e.kuaishou.com',
};
export const bizHostMap: Record<UpmBiz, Omit<Record<ENV_KEY, string>, 'local'>> = {
  [UpmBiz.财神CRM]: {
    staging: 'https://ad-crm-staging.staging.kuaishou.com',
    prt: 'https://crm-prt.prt.kuaishou.com',
    beta: 'https://beta-operation-e.corp.kuaishou.com',
    production: 'https://ad-crm.corp.kuaishou.com',
  },
  [UpmBiz.AGENT_CHANNEL]: {
    ...commonHosts,
  },
  [UpmBiz.CRM_MERCHANTS]: { ...commonHosts },
  [UpmBiz.CRM_BRAND]: {
    ...commonHosts,
  },
};

export interface openNewPageParam {
  path: string;
  targetAppId?: number;
  targetBizName?: '财神CRM' | 'CRM_BRAND' | 'CRM_MERCHANTS' | 'AGENT_CHANNEL';
  target?: '_self' | '_blank';
}
